<script setup lang="ts">
import { computed, ref } from "vue";
import { DrawerUrlOptions } from "../../../store/drawers";
import { isObject } from "vue-composable";
import { openModal, openSlideover } from "../../../composables/dialog/drawer";
import { router } from "../../../router";
import MTooltip from "../MTooltip";

const props = withDefaults(
  defineProps<{
    text?: string;
    colour?: "amber" | "green" | "red" | "blue" | "navy";

    onClick?: (e: MouseEvent) => void;
    toModal?: string | DrawerUrlOptions | object | false;
    toSlideover?: string | DrawerUrlOptions | object | false;
    to?: string;
    modalSize?: "small" | "medium" | "large" | "fullscreen";

    amber?: boolean;
    green?: boolean;
    red?: boolean;
    blue?: boolean;
    navy?: boolean;

    tooltip?: string;
    elementOverride?: string;
    // ENG-11966: wraps long content onto multiple lines instead of clipping it.
    // Set to false to force the old single-line + ellipsis behaviour.
    wrap?: boolean;
    // Caps wrapping at this many lines, ellipsis-ing anything beyond. Only
    // applies while wrap is true.
    maxLines?: number;
  }>(),
  {
    wrap: true,
    maxLines: 3,
  },
);

const badgeEl = ref<HTMLButtonElement | HTMLSpanElement | null>(null);

const element = computed(() => {
  if (props.elementOverride) {
    return props.elementOverride;
  }
  return props.onClick || props.toModal || props.toSlideover || props.to ? "button" : "span";
});

const click = computed(() => {
  if (props.onClick) {
    return props.onClick;
  }
  if (props.toModal) {
    const [url, options] = isObject(props.toModal)
      ? [props.toModal.url, props.toModal]
      : [props.toModal, {}];
    return (_e: MouseEvent) =>
      openModal(url, {
        modalSize: props.modalSize,
        ...options,
      });
  }
  if (props.toSlideover) {
    const [url, options] = isObject(props.toSlideover)
      ? [props.toSlideover.url, props.toSlideover]
      : [props.toSlideover, {}];
    return (_e: MouseEvent) => openSlideover(url, options);
  }

  if (props.to) {
    const to = props.to;
    return (_e: MouseEvent) => {
      router.push(to);
    };
  }
  return undefined;
});
</script>

<template>
  <component
    :is="element"
    v-bind="$attrs"
    ref="badgeEl"
    class="badge"
    :class="[
      {
        amber,
        green,
        red,
        blue,
        navy,
        wrap,
        'no-wrap': !wrap,
      },
      colour && colour.toLowerCase(),
    ]"
    :style="wrap ? { '--max-lines': maxLines } : undefined"
    @click.capture="
      ($event: MouseEvent) => {
        if (props.onClick || props.toModal || props.toSlideover || props.to) {
          $event.stopPropagation();
          click?.($event);
        }
      }
    "
  >
    <slot>
      {{ text }}
    </slot>
    <m-tooltip v-if="tooltip" :text="tooltip" :scroll-target="badgeEl as Element" />
  </component>
</template>

<style lang="scss">
.badge {
  display: inline-block;
  border-radius: var(--radius-default);
  padding: var(--space-1) var(--space-2);

  // <button> doesn't inherit font/appearance by default like <span> does —
  // without this a clickable badge (rendered as <button>) silently falls
  // back to the browser's default UI font/native chrome instead of matching
  // a non-clickable badge (rendered as <span>).
  appearance: none;
  font-family: inherit;
  margin: 0;

  background-color: var(--status-grey);
  color: var(--grey-darkest);
  border: 1px solid var(--grey-darkest);

  font-style: normal;
  font-weight: bold;
  font-size: var(--font-size-xs);
  line-height: 12px;

  word-wrap: break-word;
  word-break: break-word;
  width: fit-content;
  max-width: 100%;

  flex: none;
  align-self: flex-start; // ENG-21554: without this, a flex-row parent's default align-items: stretch forces the badge to match a wrapping text sibling's full height
  vertical-align: middle; // avoids the baseline descender gap inline-level boxes get in block/table contexts, and centers next to adjacent inline text

  // Tighter padding in an AG Grid cell, where column width and row height
  // are at a premium. AG Grid cells are div.ag-cell-value, not <td> — a
  // real HTML table (e.g. MJournalTimeline) is a different context and
  // shouldn't get this.
  .ag-cell-value & {
    padding-left: var(--space-1);
    padding-right: var(--space-1);
    padding-top: var(--space-1);
  }

  > .m-icon {
    width: 0.9em;
  }

  > .m-icon.fa-arrow-up,
  > .m-icon.fa-arrow-down {
    width: 0.75em;
  }

  > .m-icon:first-child {
    margin-left: -1px; // 8px padding - 1px = 7px gap when an icon leads the badge
  }

  > .m-icon + .m-icon {
    margin-left: 0.15em;
  }

  &.wrap {
    display: -webkit-inline-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--max-lines);
    overflow: hidden;
  }

  &.no-wrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.amber {
    background-color: var(--status-yellow);
    color: var(--status-yellow-text);
    border: 1px solid var(--status-yellow-text);
  }

  &.green {
    // Darker than --status-green (#008951, only 4.47:1 with white) so bold badge
    // text clears WCAG AA 4.5:1 — measured 4.95:1.
    background-color: #00814c;
    color: var(--text-on-dark);
    border-color: #00814c;
  }

  &.navy {
    background-color: var(--theme-accent-blue);
    color: var(--text-on-dark);
    border-color: var(--theme-accent-blue);
  }

  &.red {
    background-color: var(--status-red);
    color: var(--text-on-dark);
    border-color: var(--status-red);
  }

  &.blue {
    background-color: var(--status-light-blue);
    color: var(--status-blue);
    border: 1px solid var(--status-blue);
  }
}

button.badge {
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }

  &:focus {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
}
</style>
